import type { PaginatedResponse } from "../models/courseCertificate";
import type { InsurancePolicyDocumentModel } from "../models/insurancePolicy";
import {
  normalizePolicyStatus,
  type PolicyStatus,
} from "../models/policyStatus";
import { readFieldValidationFromApi } from "./fieldValidationHelpers";

type InsurancePolicyApiResponse = Record<string, unknown>;

const readString = (value: unknown): string | null =>
  typeof value === "string" && value.trim().length > 0 ? value : null;

const readNumber = (value: unknown): number | null =>
  typeof value === "number" && !Number.isNaN(value) ? value : null;

const readBoolean = (value: unknown): boolean => value === true;

const readStatus = (
  policy: InsurancePolicyApiResponse,
): PolicyStatus | undefined =>
  normalizePolicyStatus(
    policy.status ?? policy.policyStatus ?? policy.policy_status,
  );

export const mapInsurancePolicyFromApi = (
  policy: InsurancePolicyApiResponse,
): InsurancePolicyDocumentModel => {
  const documentId =
    readString(policy.documentId) ??
    readString(policy.document_id) ??
    readString(policy.id) ??
    "";

  return {
    id: readString(policy.id) ?? documentId,
    documentId,
    fileName:
      readString(policy.fileName) ??
      readString(policy.file_name) ??
      "Forsikring.pdf",
    type: readString(policy.type) ?? "insurance_policy",
    uploaded_by_id:
      readString(policy.uploaded_by_id) ??
      readString(policy.uploadedById) ??
      "",
    uploadedAt:
      readString(policy.uploadedAt) ??
      readString(policy.uploaded_at) ??
      readString(policy.createdAt) ??
      readString(policy.created_at) ??
      "",
    modelDecisionBy:
      readString(policy.modelDecisionBy) ??
      readString(policy.model_decision_by) ??
      "",
    reported: readBoolean(
      policy.reported ?? policy.isReported ?? policy.is_reported,
    ),
    reportId:
      readString(policy.reportId) ?? readString(policy.report_id) ?? null,
    status: readStatus(policy) ?? "draft",
    aiDecision:
      readString(policy.aiDecision) ?? readString(policy.ai_decision) ?? null,
    insuranceCompany:
      readString(policy.insuranceCompany) ??
      readString(policy.insurance_company) ??
      null,
    policyStartDate:
      readString(policy.policyStartDate) ??
      readString(policy.policy_start_date) ??
      null,
    lawyerLiabilityAmount:
      readNumber(policy.lawyerLiabilityAmount) ??
      readNumber(policy.lawyer_liability_amount) ??
      readNumber(policy.advokatansvar_amount) ??
      null,
    guaranteeAmount:
      readNumber(policy.guaranteeAmount) ??
      readNumber(policy.guarantee_amount) ??
      readNumber(policy.garanti_amount) ??
      null,
    fieldValidation: readFieldValidationFromApi(
      policy.fieldValidation ?? policy.field_validation,
    ),
  };
};

export const mapInsurancePoliciesPageFromApi = (
  response: PaginatedResponse<InsurancePolicyApiResponse>,
): PaginatedResponse<InsurancePolicyDocumentModel> => ({
  ...response,
  items: response.items.map(mapInsurancePolicyFromApi),
});
